// File: src/pages/CreateDepartment.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Input,
  Label,
  Form,
  Button,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";
import {
  Create_Department,
  Delete_Department,
  List_Department,
} from "../import/ApiEndPoint/Api";
import { _Delete, _GetList, _PostSave } from "../import/ApiEndPoint/ApiCalling";
import { View } from "@react-pdf/renderer";

/* =========================
   EASY TO CHANGE SETTINGS ✅
========================= */
const UI = {
  inputSize: "sm",
  textareaRows: 2,
  toolbarGap: 8,
  gridGap: 10,
  autoSaveFixedOnLoad: true,
  showBottomButtons: true,
};

const FIXED_DEPTS = [
  { name: "MANAGEMENT", desc: "Management / Admin department" },
  { name: "FINANCE", desc: "Finance / Accounts department" },
  { name: "ORDER AND BILLING", desc: "Orders, Billing & Invoicing department" },
  { name: "SALES", desc: "Sales department" },
  { name: "LOGISTIC", desc: "Logistics / Dispatch / Delivery department" },
];

const norm = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();
const isFixedName = (name) =>
  FIXED_DEPTS.some((d) => norm(d.name) === norm(name));

const injectOnce = () => {
  if (document.getElementById("dept-inline-css")) return;
  const s = document.createElement("style");
  s.id = "dept-inline-css";
  s.textContent = `
    /* ✅ Compressed UI */
    .dp-card{ border-radius:12px; border:1px solid #e5e7eb; }
    .dp-toolbar{ padding:6px 8px; background:#f4f7f6; border-radius:12px; border:1px solid #e5e7eb; }
    .dp-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:3px 8px; border-radius:999px; font-size:12px; }
    .dp-row{ border:1px solid #eef2ff; border-radius:12px; padding:10px; background:#fff; height:100%; }
    .dp-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
    .dp-help{ font-size:12px; color:#6b7280; }
    .dp-err{ font-size:12px; color:#ef4444; margin-top:4px; }
    .dp-label{ font-size:12px; margin-bottom:4px; color:#111827; font-weight:700; }

    /* ✅ Header row inside each card */
    .dp-head{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
    .dp-head-title{ font-weight:900; font-size:13px; line-height:1; }
    .dp-badges{ display:flex; gap:6px; flex-wrap:nowrap; align-items:center; }
    .dp-badge{
      font-size:10px;
      font-weight:800;
      padding:2px 6px;
      border-radius:999px;
      border:1px solid #e5e7eb;
      background:#f8fafc;
      color:#334155;
      line-height:1;
      height:18px;
      display:inline-flex;
      align-items:center;
      white-space:nowrap;
    }
    .dp-badge-fixed{ background:#eef2ff; border-color:#c7d2fe; color:#3730a3; }
    .dp-badge-mandatory{ background:#fff7ed; border-color:#fed7aa; color:#9a3412; }
    .dp-badge-new{ background:#ecfeff; border-color:#a5f3fc; color:#155e75; }
    .dp-badge-saved{ background:#f0fdf4; border-color:#bbf7d0; color:#166534; }
  `;
  document.head.appendChild(s);
};

export default function CreateDepartment() {
  useEffect(() => injectOnce(), []);

  const [formValues, setFormValues] = useState([
    { DepartmentName: "", Description: "", Id: null, _fixed: false },
  ]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const user = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    [],
  );

  // ✅ prevent repeated autosave calls on refresh
  const ensuredOnceRef = useRef(false);

  const getBadges = (row) => {
    const fixed = isFixedName(row?.DepartmentName) || row?._fixed;
    const isNew = !row?.Id;

    if (fixed) {
      return [
        { label: "Fixed", cls: "dp-badge dp-badge-fixed" },
        { label: "Mandatory", cls: "dp-badge dp-badge-mandatory" },
        { label: "Always Required", cls: "dp-badge dp-badge-mandatory" },
      ];
    }

    if (isNew) return [{ label: "New", cls: "dp-badge dp-badge-new" }];

    return [{ label: "Saved", cls: "dp-badge dp-badge-saved" }];
  };

  // ===== Load existing + ensure fixed depts exist (auto-save fixed) =====
  const loadDepartments = async () => {
    if (!user?.database) {
      setLoadingList(false);
      return;
    }

    setLoadingList(true);
    try {
      const url = `${List_Department}/${user.database}`;
      const res = await _GetList(url);
      const all = Array.isArray(res?.Department) ? res.Department : [];

      // Find a dept by name (prefer Active, else any)
      const findByName = (wantedName) => {
        const n = norm(wantedName);
        const activeMatch = all.find(
          (d) => norm(d?.departmentName) === n && d?.status === "Active",
        );
        if (activeMatch) return activeMatch;
        return all.find((d) => norm(d?.departmentName) === n) || null;
      };

      // Build fixed rows first (always show them)
      const fixedRows = FIXED_DEPTS.map((fd) => {
        const match = findByName(fd.name);
        return {
          DepartmentName: match?.departmentName || fd.name,
          Description: match?.departmentDesc || fd.desc,
          Id: match?._id || null,
          _fixed: true,
        };
      });

      // Other ACTIVE depts (excluding fixed by name)
      const otherActive = all
        .filter((d) => d?.status === "Active")
        .filter((d) => !isFixedName(d?.departmentName))
        .map((d) => ({
          DepartmentName: d?.departmentName || "",
          Description: d?.departmentDesc || "",
          Id: d?._id || null,
          _fixed: false,
        }));

      setFormValues([...fixedRows, ...otherActive]);

      // ✅ Auto-save fixed departments (creates missing; doesn't duplicate)
      if (UI.autoSaveFixedOnLoad && !ensuredOnceRef.current) {
        ensuredOnceRef.current = true;

        const missingFixed = fixedRows.filter((r) => !r?.Id);

        if (missingFixed.length > 0) {
          const payload = {
            Departments: missingFixed.map((r) => ({
              departmentName: r.DepartmentName,
              departmentDesc: r.Description,
              database: user?.database,
              departmentId: null, // create
              created_by: user?._id || user?.id || "",
            })),
          };

          try {
            await _PostSave(Create_Department, payload);

            // reload to get IDs + fresh list
            const res2 = await _GetList(url);
            const all2 = Array.isArray(res2?.Department) ? res2.Department : [];

            const findByName2 = (wantedName) => {
              const n = norm(wantedName);
              const activeMatch = all2.find(
                (d) => norm(d?.departmentName) === n && d?.status === "Active",
              );
              if (activeMatch) return activeMatch;
              return all2.find((d) => norm(d?.departmentName) === n) || null;
            };

            const fixedRows2 = FIXED_DEPTS.map((fd) => {
              const match = findByName2(fd.name);
              return {
                DepartmentName: match?.departmentName || fd.name,
                Description: match?.departmentDesc || fd.desc,
                Id: match?._id || null,
                _fixed: true,
              };
            });

            const otherActive2 = all2
              .filter((d) => d?.status === "Active")
              .filter((d) => !isFixedName(d?.departmentName))
              .map((d) => ({
                DepartmentName: d?.departmentName || "",
                Description: d?.departmentDesc || "",
                Id: d?._id || null,
                _fixed: false,
              }));

            setFormValues([...fixedRows2, ...otherActive2]);
          } catch (e) {
            console.error("[CreateDepartment] Auto-save fixed failed:", e);
          }
        }
      }
    } catch (err) {
      console.error(err);
      // fallback: show fixed only
      setFormValues(
        FIXED_DEPTS.map((fd) => ({
          DepartmentName: fd.name,
          Description: fd.desc,
          Id: null,
          _fixed: true,
        })),
      );
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Field changes =====
  const handleChange = (idx, e) => {
    const { name, value } = e.target;

    setFormValues((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], [name]: value };
      return next;
    });

    setErrors((prev) => {
      const nxt = { ...prev };
      if (nxt[idx]?.[name]) {
        const rowErr = { ...(nxt[idx] || {}) };
        delete rowErr[name];
        nxt[idx] = rowErr;
      }
      return nxt;
    });
  };

  const addFormRow = () => {
    setFormValues((prev) => [
      ...prev,
      { DepartmentName: "", Description: "", Id: null, _fixed: false },
    ]);
  };

  const removeFormRow = async (row, idx) => {
    // ✅ fixed departments cannot be removed
    if (isFixedName(row?.DepartmentName) || row?._fixed) {
      swal("This department is mandatory and cannot be removed.");
      return;
    }

    if (!row?.Id) {
      setFormValues((prev) => prev.filter((_, i) => i !== idx));
      return;
    }

    swal("Warning", "Sure you want to delete this department?", {
      buttons: {
        cancel: "Cancel",
        catch: { text: "Delete", value: "delete" },
      },
    }).then(async (value) => {
      if (value !== "delete") return;
      try {
        await _Delete(Delete_Department, row.Id);
        swal("Deleted", "Department removed", "success");
        setFormValues((prev) => prev.filter((_, i) => i !== idx));
      } catch (err) {
        console.error(err);
        swal("Error", "Failed to delete department", "error");
      }
    });
  };

  // ===== Validation (with duplicate name protection) =====
  const validate = () => {
    const rowErrors = {};
    const seen = {};

    formValues.forEach((r, i) => {
      const e = {};
      const name = String(r.DepartmentName || "").trim();
      const desc = String(r.Description || "").trim();

      if (!name) e.DepartmentName = "Department name is required";
      if (!desc) e.Description = "Description is required";

      const key = norm(name);
      if (key) {
        if (seen[key] !== undefined) {
          e.DepartmentName = "Duplicate department name";
          rowErrors[seen[key]] = {
            ...(rowErrors[seen[key]] || {}),
            DepartmentName: "Duplicate department name",
          };
        } else {
          seen[key] = i;
        }
      }

      if (Object.keys(e).length) rowErrors[i] = e;
    });

    setErrors(rowErrors);
    return Object.keys(rowErrors).length === 0;
  };

  // ===== Submit (Create/Update in bulk) =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        Departments: formValues.map((r) => ({
          departmentName: r.DepartmentName,
          departmentDesc: r.Description,
          database: user?.database,
          departmentId: r?.Id || null,
          created_by: user?._id || user?.id || "",
        })),
      };

      const res = await _PostSave(Create_Department, payload);
      if (res?.status) {
        swal("Success", "Departments saved", "success");
        localStorage.setItem("CompanyDepartments", JSON.stringify(payload));
        await loadDepartments();
      } else {
        swal("Error", res?.message || "Failed to save departments", "error");
      }
    } catch (err) {
      console.error(err);
      swal("Error", "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const countInfo = `${
    formValues.filter((r) => r.DepartmentName && r.Description).length
  }/${formValues.length} complete`;

  return (
    <>
      {/* Toolbar (compressed) */}
      <Row style={{ marginBottom: UI.gridGap }}>
        <Col sm="12">
          <div className="dp-toolbar">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: UI.toolbarGap,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  textTransform: "uppercase",
                  fontSize: 14,
                }}
              >
                Departments <span className="count-pill">{countInfo}</span>
              </div>

              <div style={{ flex: "1 1 200px" }} />

              <div className="dp-actions">
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  onClick={loadDepartments}
                >
                  Refresh
                </Button>
                <Button size="sm" color="primary" onClick={addFormRow}>
                  + Add
                </Button>
                <Button
                  size="sm"
                  color="success"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? <Spinner size="sm" /> : "Save All"}
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Body */}
      <Row>
        <Col sm="12">
          <Card className="dp-card">
            <CardBody style={{ padding: 12 }}>
              {loadingList ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "3rem 0",
                  }}
                >
                  <Spinner style={{ height: "2.5rem", width: "2.5rem" }} />
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {/* ✅ Two columns grid (outer): 1 row has 2 departments */}
                  <Row style={{ rowGap: UI.gridGap }}>
                    {formValues.map((row, idx) => {
                      const fixed =
                        isFixedName(row?.DepartmentName) || row?._fixed;
                      const badges = getBadges(row);

                      return (
                        <Col key={idx} lg="6" md="6" sm="12">
                          <div className="dp-row">
                            {/* ✅ Header row: "Department" + ALL TAGS on right */}
                            <div className="dp-head">
                              <div className="dp-head-title">Department</div>
                              <div className="dp-badges">
                                {badges.map((b, i2) => (
                                  <span key={i2} className={b.cls}>
                                    {b.label}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* ✅ Dept Name + Description in ONE ROW */}
                            <Row>
                              <Col md="6" sm="12" className="mb-2">
                                <Label className="dp-label">Dept Name *</Label>
                                <Input
                                  bsSize={UI.inputSize}
                                  name="DepartmentName"
                                  placeholder="Department Name"
                                  value={row.DepartmentName || ""}
                                  onChange={(e) => handleChange(idx, e)}
                                  disabled={fixed} // fixed name not editable
                                />
                                {errors[idx]?.DepartmentName && (
                                  <div className="dp-err">
                                    {errors[idx].DepartmentName}
                                  </div>
                                )}
                              </Col>

                              <Col md="6" sm="12" className="mb-2">
                                <Label className="dp-label">
                                  Description *
                                </Label>
                                <Input
                                  bsSize={UI.inputSize}
                                  type="textarea"
                                  rows={UI.textareaRows}
                                  name="Description"
                                  placeholder="Description"
                                  value={row.Description || ""}
                                  onChange={(e) => handleChange(idx, e)}
                                />
                                {errors[idx]?.Description && (
                                  <div className="dp-err">
                                    {errors[idx].Description}
                                  </div>
                                )}
                              </Col>

                              <Col sm="12">
                                <div className="dp-actions">
                                  {!fixed ? (
                                    <Button
                                      size="sm"
                                      color="danger"
                                      type="button"
                                      onClick={() => removeFormRow(row, idx)}
                                    >
                                      Remove
                                    </Button>
                                  ) : (
                                    <View></View>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* Bottom buttons (optional) */}
                  {UI.showBottomButtons ? (
                    <div
                      className="d-flex justify-content-end"
                      style={{ gap: 8, marginTop: 12 }}
                    >
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={loadDepartments}
                      >
                        Reset
                      </Button>
                      <Button
                        size="sm"
                        color="success"
                        type="submit"
                        disabled={saving}
                      >
                        {saving ? <Spinner size="sm" /> : "Save All"}
                      </Button>
                    </div>
                  ) : null}
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
